import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import api from '../../services/api.js';
import '../login/login.scss';
import './registration.scss';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PH_PHONE_RE = /^\+63 ?9\d{2}[ -]?\d{3}[ -]?\d{4}$/;

const FEATURES = [
  { icon: 'fa-charging-station', title: 'Nationwide Charging Plazas', text: 'Start sessions with RFID cards or the mobile app at any compatible connector.' },
  { icon: 'fa-calendar-check', title: 'Guaranteed Reservations', text: 'Lock in a charging bay for 15 or 30 minutes in advance so your spot is waiting when you arrive.' },
  { icon: 'fa-receipt', title: 'Itemized Session CDRs', text: 'Auditable Charge Detail Records break down energy kWh, bay parking, idle fees, and taxes.' },
];

function passwordStrength(value) {
  let score = 0;
  if (value.length >= 8) score += 25;
  if (/[A-Z]/.test(value)) score += 25;
  if (/[0-9]/.test(value)) score += 25;
  if (/[^A-Za-z0-9]/.test(value)) score += 25;
  if (score <= 25) return { score, color: '#ef4444', label: value ? 'Weak (Too simple)' : 'Enter password' };
  if (score <= 50) return { score, color: '#f59e0b', label: 'Fair' };
  if (score <= 75) return { score, color: '#3b82f6', label: 'Good' };
  return { score, color: '#10b981', label: 'Strong (Secure)' };
}

const isCompliantPassword = (v) => passwordStrength(v).score === 100;

export default function RegisterPage() {
  const { isAuthenticated } = useAuth();
  const [form, setForm] = useState({ fullName: '', phone: '', email: '', password: '', confirm: '' });
  const [consent, setConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [alertMsg, setAlertMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [code, setCode] = useState('');
  const [demoCode, setDemoCode] = useState('');
  const [verifyMsg, setVerifyMsg] = useState({ type: '', text: '' });
  const [verified, setVerified] = useState(false);

  if (isAuthenticated && !registeredEmail) return <Navigate to="/" replace />;

  const strength = passwordStrength(form.password);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = true;
    if (!PH_PHONE_RE.test(form.phone.trim())) next.phone = true;
    if (!EMAIL_RE.test(form.email.trim())) next.email = true;
    if (!isCompliantPassword(form.password)) next.password = true;
    if (form.confirm !== form.password) next.confirm = true;
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setAlertMsg('');
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length) {
      setAlertMsg('Please review the form errors and try again.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await api.registerDriver({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        acceptTerms: true,
      });
      // Demo environments send no email: the API hands the code back so the flow can still be completed.
      if (created?.demoVerificationCode) {
        setDemoCode(String(created.demoVerificationCode));
        setCode(String(created.demoVerificationCode));
      }
      setRegisteredEmail(form.email.trim());
    } catch (err) {
      setAlertMsg(err.message || 'Registration failed. Please try again.');
      if (/email/i.test(err.message || '')) setErrors({ email: 'in-use' });
    } finally {
      setSubmitting(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setVerifyMsg({ type: '', text: '' });
    try {
      await api.verifyEmail({ email: registeredEmail, code: code.trim() });
      setVerified(true);
    } catch (err) {
      setVerifyMsg({ type: 'danger', text: err.message || 'Invalid or expired verification code' });
    }
  };

  const onResend = async () => {
    try {
      const again = await api.resendVerification({ email: registeredEmail });
      if (again?.demoVerificationCode) { setDemoCode(String(again.demoVerificationCode)); setCode(String(again.demoVerificationCode)); }
      setVerifyMsg({ type: 'success', text: 'A new verification code has been sent.' });
    } catch (err) {
      setVerifyMsg({ type: 'danger', text: err.message || 'Could not resend the code.' });
    }
  };

  const invalid = (k) => (errors[k] ? ' is-invalid' : '');

  const brandPanel = (
    <div className="col-lg-5 d-none d-lg-flex flex-column justify-content-between p-5 text-white login-brand-panel">
      <div>
        <div className="d-flex align-items-center mb-4">
          <div className="rounded-circle d-flex align-items-center justify-content-center me-3 login-brand-icon">
            <i className="fa fa-bolt fa-lg" aria-hidden="true" />
          </div>
          <div>
            <h3 className="mb-0 fw-bold text-white">ChargeOS</h3>
            <small className="text-uppercase login-brand-tagline">Powered by Xamun</small>
          </div>
        </div>
        <h2 className="h3 fw-bold mb-3 text-white">Join the Open EV Charging Network</h2>
        <p className="mb-4 lead login-brand-lead">
          Find charging plazas, start sessions from your phone, reserve a connector ahead of time, and pay from a prepaid wallet.
        </p>
        <div className="mt-4">
          {FEATURES.map((f) => (
            <div className="d-flex align-items-start mb-3" key={f.title}>
              <div className="me-3 mt-1 login-feature-icon"><i className={`fa ${f.icon} fa-fw`} aria-hidden="true" /></div>
              <div>
                <h6 className="mb-1 fw-bold text-white">{f.title}</h6>
                <small className="login-feature-text">{f.text}</small>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="pt-4 border-top small d-flex justify-content-between align-items-center login-brand-footer">
        <span className="login-feature-text">&copy; 2026 ChargeOS Inc. All rights reserved.</span>
        <span className="badge login-ocpp-badge">OCPP 1.6-J / 2.0.1</span>
      </div>
    </div>
  );

  const field = (id, label, icon, props, feedback, extra) => (
    <div className="mb-3">
      <label htmlFor={id} className="form-label small fw-bold">{label}</label>
      <div className="input-group has-validation">
        <span className="input-group-text bg-light border-end-0"><i className={`fa ${icon} text-muted`} aria-hidden="true" /></span>
        <input
          id={id}
          className={`form-control border-start-0${invalid(props.errKey)}`}
          aria-invalid={!!errors[props.errKey]}
          aria-describedby={`${id}-feedback`}
          required
          {...props.input}
        />
        {extra}
        <div id={`${id}-feedback`} className="invalid-feedback">{feedback}</div>
      </div>
    </div>
  );

  let right;
  if (registeredEmail) {
    right = (
      <div id="verification-panel">
        <div className="text-center mb-4">
          <i className="fa fa-circle-check fa-3x mb-3 text-success" aria-hidden="true" />
          <h1 className="h3 fw-bold text-primary" id="registration-success-title">
            {verified ? 'Email verified' : 'Account created'}
          </h1>
          <p className="text-muted" id="registration-success-msg">
            {verified
              ? 'Your email is verified and your prepaid wallet is ready. You can now sign in.'
              : <>Your driver account and prepaid wallet (₱0.00) have been created. We sent a verification code to <strong>{registeredEmail}</strong>. Enter it below to verify your email.</>}
          </p>
        </div>
        {verified ? (
          <Link to="/login" className="btn btn-primary w-100 fw-bold py-2" id="btn-go-login">Go to Sign In</Link>
        ) : (
          <form onSubmit={onVerify} noValidate>
            {demoCode && (
              <div className="alert alert-info py-2 small" role="note" id="demo-verification-note">
                <i className="fa fa-flask me-1" aria-hidden="true" />Demo: no email is sent. Your verification code is <strong>{demoCode}</strong>.
              </div>
            )}
            {verifyMsg.text && <div className={`alert alert-${verifyMsg.type}`} role="alert" id="verify-alert">{verifyMsg.text}</div>}
            <label htmlFor="verification_code" className="form-label small fw-bold">Verification Code</label>
            <input
              id="verification_code"
              className="form-control mb-3"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button type="submit" id="btn-verify-submit" className="btn btn-primary w-100 fw-bold py-2 mb-2" disabled={code.trim().length !== 6}>
              Verify Email
            </button>
            <button type="button" id="btn-resend-code" className="btn btn-link w-100 small" onClick={onResend}>Resend code</button>
            <div className="text-center mt-2">
              <Link to="/login" className="small fw-bold text-primary">Verify later and sign in</Link>
            </div>
          </form>
        )}
      </div>
    );
  } else {
    right = (
      <>
        <div className="d-lg-none d-flex align-items-center mb-4">
          <i className="fa fa-bolt fa-2x me-2 text-primary" aria-hidden="true" />
          <h4 className="mb-0 fw-bold text-primary">ChargeOS</h4>
        </div>
        <div className="mb-4">
          <h1 className="h3 fw-bold mb-2 text-primary">Create Your Driver Account</h1>
          <p className="text-muted">Register to charge your EV and manage your prepaid wallet.</p>
        </div>

        <div id="register-alert" className={`alert alert-danger${alertMsg ? '' : ' d-none'}`} role="alert" aria-live="polite">
          <i className="fa fa-circle-exclamation me-2" aria-hidden="true" />
          <span id="register-alert-msg">{alertMsg}</span>
        </div>

        <form id="registration-form" noValidate onSubmit={onSubmit}>
          <div className="row">
            <div className="col-md-6">
              {field('full_name', 'Full Name', 'fa-user',
                { errKey: 'fullName', input: { type: 'text', name: 'full_name', placeholder: 'Juan Dela Cruz', autoComplete: 'name', autoFocus: true, value: form.fullName, onChange: set('fullName') } },
                'Full name is required.')}
            </div>
            <div className="col-md-6">
              {field('phone_number', 'Mobile Phone', 'fa-phone',
                { errKey: 'phone', input: { type: 'tel', name: 'phone_number', placeholder: '+63 917 123 4567', autoComplete: 'tel', value: form.phone, onChange: set('phone') } },
                'Valid Philippine mobile number required (+63).')}
            </div>
          </div>
          {field('email', 'Email Address', 'fa-envelope',
            { errKey: 'email', input: { type: 'email', name: 'email', placeholder: 'driver@example.ph', autoComplete: 'email', value: form.email, onChange: set('email') } },
            errors.email === 'in-use' ? 'This email address is already in use.' : 'A valid email address is required.')}

          <div className="mb-3">
            <label htmlFor="password_hash" className="form-label small fw-bold">Password</label>
            <div className="input-group has-validation">
              <span className="input-group-text bg-light border-end-0"><i className="fa fa-lock text-muted" aria-hidden="true" /></span>
              <input
                id="password_hash"
                name="password_hash"
                type={showPassword ? 'text' : 'password'}
                className={`form-control border-start-0 border-end-0${invalid('password')}`}
                placeholder="Min. 8 characters"
                autoComplete="new-password"
                required
                aria-invalid={!!errors.password}
                aria-describedby="password-policy password-strength-live"
                value={form.password}
                onChange={set('password')}
              />
              <button className="btn btn-outline-secondary border-start-0" type="button" id="toggle-reg-password" aria-label="Toggle password visibility" onClick={() => setShowPassword((v) => !v)}>
                <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`} aria-hidden="true" />
              </button>
              <div className="invalid-feedback">Password must be at least 8 characters with an uppercase letter, a number and a symbol.</div>
            </div>
            <small id="password-policy" className="form-text text-muted">Min 8 chars with uppercase, number &amp; symbol.</small>
            <div className="progress mt-2 password-strength-track">
              <div id="password-strength-bar" className="progress-bar" role="progressbar" style={{ width: `${strength.score}%`, backgroundColor: strength.color }} aria-valuenow={strength.score} aria-valuemin="0" aria-valuemax="100" />
            </div>
            <small id="password-strength-live" className="d-block mt-1 fw-bold password-strength-text" style={{ color: form.password ? strength.color : undefined }} aria-live="polite">
              Strength: {strength.label}
            </small>
          </div>

          {field('confirm_password', 'Confirm Password', 'fa-lock',
            { errKey: 'confirm', input: { type: showPassword ? 'text' : 'password', name: 'confirm_password', placeholder: 'Repeat password', autoComplete: 'new-password', value: form.confirm, onChange: set('confirm') } },
            'Passwords must match.')}

          <div className="form-check mb-4">
            <input type="checkbox" className="form-check-input" id="terms-consent" required checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <label className="form-check-label text-muted small" htmlFor="terms-consent">
              I agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer" className="fw-bold text-primary">Terms of Service</Link> and acknowledge the <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="fw-bold text-primary">Privacy Policy</Link> (Data Privacy Act 2012).
            </label>
          </div>

          <button type="submit" id="btn-register-submit" className="btn btn-primary w-100 fw-bold py-2 mb-3" disabled={!consent || submitting}>
            {submitting && <span id="btn-reg-spinner" className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
            <span id="btn-reg-text">{submitting ? 'Creating Account...' : 'Create Driver Account'}</span>
          </button>

          <div className="text-center mt-3">
            <span className="text-muted small">Already have an account? </span>
            <Link to="/login" className="fw-bold small text-primary">Sign In</Link>
          </div>
        </form>
      </>
    );
  }

  return (
    <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center py-5">
      <div className="row w-100 justify-content-center">
        <div className="col-12 col-xl-11">
          <div className="card overflow-hidden border shadow-lg login-card">
            <div className="row g-0">
              {brandPanel}
              <div className="col-12 col-lg-7 p-4 p-md-5 d-flex flex-column justify-content-center bg-white">{right}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
